import React from 'react';
import {
  Paper,
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import FilterListIcon from '@mui/icons-material/FilterList';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import { useCatalogContext } from './CatalogContext';
import { useGetFilters } from '../../../../client/hooks/marketplace/marketplace.queries';

const CatalogFilterPanel = () => {
  const {
    filters,
    handleFilterChange,
    handleSearch,
    handleResetFilters,
    handleDrawerToggle
  } = useCatalogContext();
  
  const { data, isLoading } = useGetFilters(true);
  const categorias = data?.categories || [];
  
  const isMobile = window.innerWidth < 600;
  const isTablet = window.innerWidth < 900;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        mb: 3,
        border: "1px solid #e0e0e0",
        borderRadius: "12px",
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Filtrar produtos
          </Typography>
        </Box>

        {!isMobile && (
          <Box>
            <Button
              variant="text"
              size="small"
              onClick={handleResetFilters}
              sx={{ color: 'text.secondary', textTransform: 'none' }}
            >
              Limpar filtros
            </Button>
          </Box>
        )}
      </Box>

      {/* Filtros principais - sempre visíveis */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: !isMobile ? 2 : 0 }}>
        <Box sx={{ flex: '1 1 250px' }}>
          <TextField
            fullWidth
            label="Nome do Produto"
            name="nome"
            value={filters.nome}
            onChange={handleFilterChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
            sx={{
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#f7d0bf"
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#f7d0bf"
                }
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#000"
              }
            }}
          />
        </Box>
        <Box sx={{ flex: '1 1 250px' }}>
          <TextField
            fullWidth
            label="Código do Produto"
            name="codigo"
            value={filters.codigo}
            onChange={handleFilterChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InventoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
            sx={{
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#f7d0bf"
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#f7d0bf"
                }
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#000"
              }
            }}
          />
        </Box>

        {/* Estes filtros são visíveis apenas em telas médias e grandes */}
        {!isTablet && (
          <>
            <Box sx={{ flex: '1 1 200px' }}>
              <FormControl fullWidth size="small" sx={{ bgcolor: '#fff', borderRadius: 2 }}>
                <InputLabel>Categoria</InputLabel>
                <Select
                  name="categoria"
                  value={filters.categoria || ""}
                  onChange={handleFilterChange}
                  label="Categoria"
                  sx={{
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      "&:hover": {
                        borderColor: "#f7d0bf"
                      }
                    }
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <CategoryIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">Todas as Categorias</MenuItem>
                  {categorias.map(categoria => {
                    // Suporte robusto para string, objeto, ou outros formatos
                    let value = categoria.id || categoria.name || categoria;
                    let label = categoria.name || categoria;
                    if (typeof label === 'string') {
                      label = label
                        .toLowerCase()
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, l => l.toUpperCase());
                    }
                    // O value agora é apenas o id da categoria
                    return (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px' }}>
              <FormControl fullWidth size="small" sx={{ bgcolor: '#fff', borderRadius: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  label="Status"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="ativo">Ativos</MenuItem>
                  <MenuItem value="inativo">Inativos</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        <Box sx={{ flex: isTablet ? '1 1 auto' : '0 0 auto', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            fullWidth={isTablet}
            sx={{
              bgcolor: "#f7d0bf",
              color: "black",
              borderRadius: 2,
              py: 1,
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(247, 208, 191, 0.4)',
              flexGrow: isTablet ? 1 : 0,
              "&:hover": {
                bgcolor: "#f5c1a9",
                boxShadow: '0 4px 12px rgba(247, 208, 191, 0.6)',
              },
            }}
          >
            Buscar
          </Button>
          {isTablet && (
            <Button
              variant="outlined"
              onClick={handleDrawerToggle}
              sx={{
                borderColor: '#e0e0e0',
                borderRadius: 2,
                py: 1,
                minWidth: '44px',
                width: '44px'
              }}
            >
              <FilterListIcon />
            </Button>
          )}
        </Box>
      </Box>

      {/* Filtros avançados - visíveis apenas em telas grandes */}
      {!isTablet && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mt: 1,
            pt: 2,
            borderTop: '1px dashed #e0e0e0'
          }}
        >
          <Box sx={{ flex: '1 1 200px' }}>
            <TextField
              fullWidth
              label="Preço Mínimo"
              name="precoMin"
              value={filters.precoMin}
              onChange={handleFilterChange}
              size="small"
              type="number"
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                inputProps: { min: 0 },
                sx: { borderRadius: 2 }
              }}
              sx={{ backgroundColor: '#fff' }}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px' }}>
            <TextField
              fullWidth
              label="Preço Máximo"
              name="precoMax"
              value={filters.precoMax}
              onChange={handleFilterChange}
              size="small"
              type="number"
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                inputProps: { min: 0 },
                sx: { borderRadius: 2 }
              }}
              sx={{ backgroundColor: '#fff' }}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px' }}>
            <FormControl fullWidth size="small" sx={{ bgcolor: '#fff', borderRadius: 2 }}>
              <InputLabel>Estoque</InputLabel>
              <Select
                name="estoque"
                value={filters.estoque}
                onChange={handleFilterChange}
                label="Estoque"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="disponivel">Disponível</MenuItem>
                <MenuItem value="baixo">Estoque Baixo</MenuItem>
                <MenuItem value="indisponivel">Indisponível</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default CatalogFilterPanel;
